/**
 * @fileoverview One practice recording, reviewed from the student's page
 * (iOS review B5) at `/students/:studentId/practice/:recordingId`.
 *
 * What Hans heard, what is going well and what to work on, then the
 * instructor's own feedback. There is no single-recording endpoint, so the
 * page reads the student's list (`GET /students/{id}/practice-recordings`)
 * and picks the recording out of it; an id that is not there (or a list the
 * server refuses, as it does with rehearsals off) reads as unavailable.
 *
 * No player, on purpose: rehearsal audio is deleted once Hans has listened,
 * and instructors never receive it.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import {
  practiceAiUpgradeTier,
  practiceRecordingEyebrow,
  practiceRecordingState,
} from "@/lib/practiceRecordings";
import { FIELD_CLS } from "@/components/studio/EditableList";
import { Button, Card, Eyebrow, Icon, PageShell } from "@/components/studio/primitives";

/** One labelled block of the review. */
function Section({ title, testId, children }) {
  return (
    <section className="mt-9" data-testid={testId}>
      <Eyebrow>{title}</Eyebrow>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="flex list-disc flex-col gap-2 pl-5 font-sans text-[16px] leading-[1.5] text-[var(--text-primary)] marker:text-[var(--text-tertiary)]">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

const PracticeRecordingPage = () => {
  const { studentId, recordingId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | ready | missing
  const [recording, setRecording] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    api
      .get(`/students/${studentId}/practice-recordings`, { signal: controller.signal })
      .then((res) => {
        const found = (Array.isArray(res.data) ? res.data : []).find((r) => r.recording_id === recordingId);
        setRecording(found || null);
        setFeedback(found?.instructor_notes || "");
        setStatus(found ? "ready" : "missing");
      })
      .catch(() => {
        if (!controller.signal.aborted) setStatus("missing");
      });
    return () => controller.abort();
  }, [studentId, recordingId]);

  const saveFeedback = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(`/lessons/practice-recordings/${recordingId}`, { instructor_notes: feedback });
      setRecording((current) => ({ ...current, instructor_notes: feedback }));
      toast.success("Feedback saved");
    } catch (error) {
      setSaveError(getErrorMessage(error, "Couldn't save your feedback. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const backButton = (
    <Button variant="bare" icon="chevronLeft" onPress={() => navigate(`/students/${studentId}`)} data-testid="back-to-student">
      Back
    </Button>
  );

  if (status === "loading") {
    return (
      <PageShell>
        {backButton}
        <div className="mt-7 space-y-4">
          <div className="skeleton h-4 w-56 rounded" />
          <div className="skeleton h-10 w-80 rounded" />
        </div>
      </PageShell>
    );
  }

  if (status === "missing" || !recording) {
    return (
      <PageShell data-testid="practice-recording-missing">
        {backButton}
        <Card className="mt-7 text-center" style={{ padding: "40px 20px" }}>
          <Icon name="mic" size={30} className="mx-auto mb-3 text-[var(--text-quaternary)]" />
          <p className="font-sans text-[15px] text-[var(--text-secondary)]">This recording isn&rsquo;t available.</p>
        </Card>
      </PageShell>
    );
  }

  const processing = practiceRecordingState(recording) === "processing";
  const failed = recording.processing_status === "failed";
  const upgradeTier = practiceAiUpgradeTier();
  const strengths = recording.ai_strengths || [];
  const improvements = recording.ai_improvements || [];
  const dirty = feedback !== (recording.instructor_notes || "");

  return (
    <PageShell data-testid="practice-recording-page">
      {backButton}

      <header className="mt-7">
        <Eyebrow>{practiceRecordingEyebrow(recording)}</Eyebrow>
        <h1 className="mt-3 font-serif text-[32px] font-normal leading-[1.15] tracking-[-0.02em] text-[var(--text-primary)] md:text-[40px]">
          {recording.title || "Practice recording"}
        </h1>
        {recording.lesson_id && (
          <Link
            to={`/lesson/${recording.lesson_id}`}
            className="mt-3 inline-flex items-center gap-1.5 font-sans text-[14px] font-semibold text-[var(--accent-yellow-text)] hover:underline"
            data-testid="practice-lesson-link"
          >
            After this lesson <Icon name="chevronRight" size={15} strokeWidth={2} />
          </Link>
        )}
      </header>

      {processing && (
        <p className="mt-9 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="practice-processing">
          Hans is still listening to this one.
        </p>
      )}

      {failed && (
        <p className="mt-9 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="practice-failed">
          Hans couldn&rsquo;t process this recording.
        </p>
      )}

      {recording.processing_status === "complete" && recording.ai_skipped && (
        <p className="mt-9 font-sans text-[15px] leading-[1.5] text-[var(--text-tertiary)]" data-testid="practice-ai-skipped">
          AI review isn&rsquo;t included on your current plan. Add your own feedback below.
          {upgradeTier && <> Upgrade to {upgradeTier} for automatic AI analysis.</>}
        </p>
      )}

      {recording.processing_status === "complete" && !recording.ai_skipped && (
        <>
          {recording.ai_notes && (
            <Section title="Hans heard" testId="practice-heard">
              <p className="whitespace-pre-wrap font-serif text-[19px] leading-[1.6] text-[var(--text-primary)]">
                {recording.ai_notes}
              </p>
            </Section>
          )}
          {strengths.length > 0 && (
            <Section title="Going well" testId="practice-going-well">
              <Bullets items={strengths} />
            </Section>
          )}
          {improvements.length > 0 && (
            <Section title="To work on" testId="practice-to-work-on">
              <Bullets items={improvements} />
            </Section>
          )}
        </>
      )}

      <Card className="mt-10" data-testid="practice-feedback">
        <Eyebrow>Your feedback</Eyebrow>
        <textarea
          aria-label="Your feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What to keep, and what to try next"
          rows={5}
          className={`${FIELD_CLS} mt-3 min-h-[120px] text-[15px] leading-[1.5]`}
          data-testid="practice-feedback-input"
        />
        {saveError && (
          <p role="alert" className="mt-3 font-sans text-[13px] text-[var(--state-danger)]" data-testid="practice-feedback-error">
            {saveError}
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <Button onPress={saveFeedback} disabled={saving || !dirty} data-testid="practice-feedback-save">
            {saving ? "Saving…" : "Save feedback"}
          </Button>
        </div>
      </Card>
    </PageShell>
  );
};

export default PracticeRecordingPage;
